import {useContainer} from '#imports';
import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import type ProSkillManagerMapper from '~~/package/mapper/proSkillManager/ProSkillManagerMapper';
import {
    ProSkillManagerAssessmentModel
} from '~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentModel';
import {PageModel} from '~~/package/mapper/common/model/PageModel';
import type {
    ProSkillManagerCandidateModel
} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateModel';
import {ProSkillManagerType} from '~~/package/mapper/proSkillManager/model/ProSkillManagerType';
import {ProSkillMangerReportModel} from '~~/package/mapper/proSkillManager/model/report/ProSkillManagerReportModel';
import {
    ProSkillManagerSubmissionModel
} from '~~/package/mapper/proSkillManager/model/submission/ProSkillManagerSubmissionModel';
import type {
    ProSkillManagerSummaryModel
} from '~~/package/mapper/proSkillManager/model/summary/ProSkillManagerSummaryModel';
import {
    ProSkillManagerAssessmentDetailInfoModel
} from '~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentDetailInfoModel';
import type ProSkillManagerTrackResponse from '~~/package/apis/ProSkillManager/response/ProSkillManagerTrackResponse';
import {
    ProSkillManagerTimelineModel
} from '~~/package/mapper/proSkillManager/model/actions/ProSkillManagerTimelineModel';
import {
    monitorAssessmentDetailPage,
    monitorCandidateSummary,
    monitorSampleAssessmentTasks,
    monitorSampleReportData,
    monitorSampleSubmissionData
} from '@/components/ProSkillManager/main/content/sample/ProSkillMornitorSampleData';
import {getFilteredSampleCandidatePage} from '@/components/ProSkillManager/main/content/sample/ProSkillMornitorSampleFilter';
import {
    ProSkillManagerAssessmentCandidateSummaryModel
} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerAssessmentCandidateSummaryModel';

export const useProSkillManagerService = () => {
    const proSkillManagerMapper = useContainer<ProSkillManagerMapper>(DependencyInjectId.ProSkillManagerMapper);

    const getProSkillManagerAssessment = (lang: string, page: number, itemSize: number, type?: ProSkillManagerType, filters?: string[], title?: string): Promise<PageModel<ProSkillManagerAssessmentModel>> => {
        return proSkillManagerMapper.getProSkillManagerAssessment(lang, page, itemSize, type, filters, title);
    };

    const getProSkillManagerAssessmentDetailInfo = (id: number): Promise<ProSkillManagerAssessmentDetailInfoModel> => {
        if(id === 0) {
            return new Promise(resolve => {
                return resolve(ProSkillManagerAssessmentDetailInfoModel.create(monitorAssessmentDetailPage.info))
            });
        } else {
            return proSkillManagerMapper.getProSkillManagerAssessmentDetailInfo(id);
        }
    };

    const getProSkillManagerCandidate = (lang: string, page: number, size: number, sort?: string, assessmentId?:number, filter?: string, status?: string[]): Promise<PageModel<ProSkillManagerCandidateModel>> => {
        if(assessmentId === 0) {
            return new Promise(resolve => {
                if (filter === 'integrity=true') {
                    return resolve(new PageModel<ProSkillManagerCandidateModel>([], true, 1, 0));
                }
                return resolve(getFilteredSampleCandidatePage(filter, status, page, size));
            });
        } else {
            return proSkillManagerMapper.getProSkillManagerCandidate(lang, page, size, sort, assessmentId, filter, status);
        }
    };

    const getProSkillManagerCandidateSummary = (lang: string, assessmentId: number): Promise<ProSkillManagerAssessmentCandidateSummaryModel> => {
        if(assessmentId === 0) {
            return new Promise(resolve => {
                return resolve(ProSkillManagerAssessmentCandidateSummaryModel.create(monitorCandidateSummary))
            });
        } else {
            return proSkillManagerMapper.getProSkillManagerCandidateSummary(lang, assessmentId);
        }
    };

    const getProSkillManagerTimeline = (assessmentId: number, userUUID: string): Promise<ProSkillManagerTimelineModel> => {
        if(assessmentId === 0) {
            return new Promise(resolve => {
                return resolve(ProSkillManagerTimelineModel.create(monitorAssessmentDetailPage.history[userUUID]))
            });
        } else {
            return proSkillManagerMapper.getProSkillManagerTimeline(assessmentId, userUUID);
        }
    }

    const getProSkillManagerReport = (lang: string, userUUID: string, assessmentId: number): Promise<ProSkillMangerReportModel> => {
        if(assessmentId === 0) {
            return new Promise(resolve => {
                return resolve(ProSkillMangerReportModel.create(monitorSampleReportData[userUUID]))
            });
        } else {
            return proSkillManagerMapper.getProSkillManagerReport(lang, userUUID, assessmentId);
        }
    }

    const getProSkillManagerSubmission = (lang: string, userUUID: string, assessmentId: number, testTemplateId: number): Promise<ProSkillManagerSubmissionModel> => {
        if(assessmentId === 0) {
            return new Promise(resolve => {
                return resolve(ProSkillManagerSubmissionModel.create(monitorSampleSubmissionData[userUUID][testTemplateId]))
            });
        } else {
            return proSkillManagerMapper.getProSkillManagerSubmission(lang, userUUID, assessmentId, testTemplateId);
        }
    }

    const getProSkillManagerSummary = (timeUnit: string, timeAmount: number, type?: ProSkillManagerType, excludeStat?: boolean): Promise<ProSkillManagerSummaryModel> => {
        return proSkillManagerMapper.getProSkillManagerSummary(timeUnit, timeAmount, type, excludeStat);
    };

    const getProSkillManagerTrack = (type?: ProSkillManagerType): Promise<ProSkillManagerTrackResponse> => {
        return proSkillManagerMapper.getProSkillManagerTrack(type);
    };

    const getAssessmentTasks = (customAssessmentId: number): Promise<Array<{trackTitle: string, trackLevelTitle: string, taskIds: number[]}>> => {
        if (customAssessmentId === 0) {
            return Promise.resolve(monitorSampleAssessmentTasks);
        }
        return proSkillManagerMapper.getAssessmentTasks(customAssessmentId);
    };

    const getPromptHistory = (userUUID: string, domain: string, domainId: string | number, taskId: string | number): Promise<{agentInstruction: string | null; items: Array<{ts: number; message: string | null; response: string | null; promptToken: number | null; totalToken: number | null; status: string | null}>}> => {
        return proSkillManagerMapper.getPromptHistory(userUUID, domain, domainId, taskId);
    };

    return {
        getProSkillManagerAssessment,
        getProSkillManagerAssessmentDetailInfo,
        getProSkillManagerCandidate,
        getProSkillManagerCandidateSummary,
        getProSkillManagerTimeline,
        getProSkillManagerReport,
        getProSkillManagerSummary,
        getProSkillManagerSubmission,
        getProSkillManagerTrack,
        getAssessmentTasks,
        getPromptHistory
    };
}
